// noinspection NestedFunctionJS

/**
 * The Wrapper loader.
 * Loads the wrapper scripts, CSS, and all necessary JSONs.
 * Transpiles as wrapper-<platform>.js
 */
import { ISettingsConfig } from "@/interfaces/ISettingsConfig";

((d = document, l = location, n = navigator, w = window): void => {
    const initialScript = d.getElementById("wrapper") as HTMLScriptElement;
    const initialScriptSrc = initialScript.src;
    d.head.removeChild(initialScript);

    const scriptSrcOfChangedVersion = getScriptSrcOfChangedVersion(); // wrapper version is checked on the game-level, but only for dev; this one is for prod
    if (scriptSrcOfChangedVersion) {
        const script = d.createElement("script");
        script.id = "wrapper";
        script.src = scriptSrcOfChangedVersion;
        d.head.appendChild(script);

        return;
    }

    const platform = getPlatform();
    const minSuffix = getMinSuffix();
    const pathToWrapper = /^(.+)wrapper-/.exec(initialScriptSrc)[1];
    const stgTail = /\.stg/.exec(initialScriptSrc) ? "?.stg" : "";
    const es5Suffix = "noModule" in d.createElement("script") ? "" : ".es5";
    const isLiveGame = /\blive=(1|true)/i.test(l.href);

    loadJs("main", "wrapper", stgTail);
    loadJs("vendors", "vendors");
    loadCSS();

    loadJSON("settings.json", callbackAfterSettingsJson);
    loadJSON("device.json");
    loadJSON("languages.json");
    if (!isLiveGame) {
        loadJSON("version.json");
    }

    function callbackAfterSettingsJson(content: ISettingsConfig): void {
        const timestamp = Date.now();
        const root = content.root || "/";
        const pathsToLoad = [`${root}config/server.json`, `${root}modules/versions/global.json?${timestamp}`];
        if (!isLiveGame) {
            const splashName = new URL(l.href).searchParams.get("splash") || "default";
            if (splashName !== "0") {
                pathsToLoad.push(`${root}splash/${splashName}/splash.json`);
            }
            let gameCode = content.ID;
            if (/\bstartGameToken=([\w.-]+)/i.test(l.href)) {
                gameCode = JSON.parse(atob(RegExp.$1.split(".")[1].replace("-", "+").replace("_", "/")))
                    .gameCode as string;
            }
            pathsToLoad.push(`${root}modules/versions/${gameCode}.json?${timestamp}`);
        }
        let remainingLoads = pathsToLoad.length;
        pathsToLoad.forEach(function (path) {
            loadJSON(path, function () {
                remainingLoads--;
                if (remainingLoads === 0) {
                    if (w["jsonsLoadedCallback"]) {
                        w["jsonsLoadedCallback"](); // the wrapper is ready and waiting
                    } else {
                        w["jsonsLoaded"] = true; // the wrapper is not ready
                    }
                }
            });
        });
    }

    function getMinSuffix(): string {
        const min = ".min";
        const full = "";
        /\bfwb=(\w+)/.test(l.href); // Forced min (forced wrapper build): "&fwb=min"/"&fwb=full"
        const urlParam = RegExp.$1;
        if (urlParam === "min") {
            return min;
        }
        if (urlParam === "full") {
            return full;
        }

        return /\.min/.exec(initialScriptSrc) ? min : full;
    }

    function getPlatform(): string {
        /\bplm=(\w+)/.test(l.href); // Forced mobile: "&plm=m"
        const urlParam = RegExp.$1;
        if (urlParam === "m") {
            return "mobile";
        }
        const p = n.platform;
        const ua = n.userAgent.toLowerCase();
        const isIPadOS = p === "MacIntel" && "ontouchstart" in window;
        const isIOS = p.slice(0, 2) === "iP" || /ip(hone|od|ad)/.test(ua);
        const isAndroid = /\bandroid\b/.test(ua) && ua.indexOf("windows nt") === -1;

        const isMobile = isIPadOS || isAndroid || isIOS || isMobileExtra();

        return isMobile ? "mobile" : "desktop";
    }

    function getScriptSrcOfChangedVersion(): string {
        const urlVersionRegex = /\bwrapper=([\w-.]+)/.exec(l.href);
        const currentVersionRegex = /\bwrapper\/([\w-.]+)\/wrapper/.exec(initialScriptSrc);
        if (urlVersionRegex && urlVersionRegex[1] !== currentVersionRegex[1]) {
            return initialScriptSrc.replace(currentVersionRegex[1], urlVersionRegex[1]);
        }
    }

    /**
     * Additional detecting mobile device for some special cases when user agent can be faked
     * For example, when enable "desktop" mode on browser
     * @return {boolean}
     */
    function isMobileExtra(): boolean {
        // detect some notebooks with arm processor and sensor screen with Linux as a mobile device
        return /Linux (armv[78]l|aarch64)/.test(n.platform) && n.maxTouchPoints > 1;
    }

    function loadCSS(): void {
        const link = d.createElement("link");
        link.href = `${pathToWrapper}css/wrapper.${platform}${minSuffix}.css`;
        link.rel = "stylesheet";
        d.head.appendChild(link);
    }

    function loadJs(name: string, id: string, tail?: string): void {
        const script = d.createElement("script");
        script.id = id;
        script.async = true;
        let fullPath = `${pathToWrapper}wrapper.${platform}.${name}${es5Suffix}${minSuffix}.js`;
        if (tail) {
            fullPath += tail;
        }
        script.src = fullPath;
        d.head.appendChild(script);
    }

    /**
     * Loads a .json file, parses it and stores the content into a global variable.
     * The file can be already (or failed to load) by the game level wrapper-loading.js
     * @example content of "/settings.json" stores as window["settings.json"]
     * @example content of "/modules/versions/sw_aa.json?123423" stores as window["sw_aa.json"]
     */
    function loadJSON(path: string, callback?: (content: unknown) => void): void {
        const globalName = path
            .replace(/\?\d*$/, "")
            .split("/")
            .pop();
        if (w[globalName] !== undefined /* the json is already loaded by the game level wrapper-loading.js */) {
            if (callback) {
                callback(w[globalName]);
            }

            return;
        }
        const xhr = new XMLHttpRequest();
        xhr.open("GET", path);
        xhr.onreadystatechange = (): void => {
            if (xhr.readyState === 4) {
                let parsedData: boolean | unknown = false;
                if (xhr.status === 200 && xhr.getResponseHeader("Content-Length") !== "0") {
                    try {
                        parsedData = JSON.parse(xhr.responseText) as unknown;
                    } catch {}
                }
                w[globalName] = parsedData; // saves as FALSE if the file is missing or data are broken
                if (callback) {
                    callback(parsedData);
                }
            }
        };
        xhr.send();
    }
})();
